import React from 'react';
import { Cpu, ListChecks, HardDrives, Question, Sparkle } from '@phosphor-icons/react';

const Panel = ({ icon: Icon, title, testId, children }) => (
  <section className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6 mb-6" data-testid={testId}>
    <h2 className="flex items-center gap-2 text-lg font-['JetBrains_Mono'] font-bold text-white mb-4">
      <Icon size={20} className="text-cyan-400" weight="duotone" /> {title}
    </h2>
    {children}
  </section>
);

export const ProductAiSpecs = ({ specs, status, name }) => {
  if (!specs) {
    return (
      <div className="bg-[#11141D] border border-dashed border-[#334155] rounded-lg p-6 mb-6 text-sm font-mono text-[#94A3B8]" data-testid="ai-specs-pending">
        <Sparkle size={16} className="inline mr-2 text-cyan-400" />
        {status === 'failed' ? 'Detailed technical specs are temporarily unavailable for this part.' : 'Detailed module architecture & technical specs are being generated for this part.'}
      </div>
    );
  }
  return (
    <div data-testid="ai-specs">
      {specs.summary && (
        <p className="text-[#CBD5E1] text-base leading-relaxed mb-6" data-testid="ai-summary">{specs.summary}</p>
      )}
      <Panel icon={ListChecks} title="Technical Specifications" testId="ai-specs-table">
        <dl className="grid sm:grid-cols-2 gap-x-8">
          {specs.specs.map((s, i) => (
            <div key={s.label} className="flex justify-between gap-4 py-2.5 border-b border-[#1E293B] text-sm" data-testid={`spec-row-${i}`}>
              <dt className="font-mono uppercase text-xs tracking-wider text-[#94A3B8] pt-0.5">{s.label}</dt>
              <dd className="font-mono text-white text-right">{s.value}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      {specs.architecture && (
        <Panel icon={Cpu} title="Module Architecture" testId="ai-architecture">
          {specs.architecture.split(/\n+/).map((p, i) => (
            <p key={i} className="text-[#94A3B8] text-sm leading-relaxed mb-3 last:mb-0">{p}</p>
          ))}
        </Panel>
      )}
      {specs.compatible_platforms?.length > 0 && (
        <Panel icon={HardDrives} title="Typical Compatible Platforms" testId="ai-platforms">
          <div className="flex flex-wrap gap-2">
            {specs.compatible_platforms.map((p) => (
              <span key={p} className="px-3 py-1.5 rounded bg-[#05050A] border border-[#334155] text-xs font-mono text-[#CBD5E1]">{p}</span>
            ))}
          </div>
          <p className="text-[11px] text-[#64748B] mt-3 font-mono">Always verify against your server's qualified vendor list (QVL).</p>
        </Panel>
      )}
      {specs.faq?.length > 0 && (
        <Panel icon={Question} title={`${name} FAQ`} testId="ai-faq">
          {specs.faq.map((f, i) => (
            <details key={i} className="border-b border-[#1E293B] py-3 group" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer text-sm font-mono text-white group-open:text-cyan-400 transition-colors">{f.q}</summary>
              <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </Panel>
      )}
    </div>
  );
};

export default ProductAiSpecs;
